import { useMutation } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { api } from "@/api/client";
import { useAuth } from "@/auth/AuthContext";
import { isModuleEnabled, MODULES } from "@/auth/modules";

/** 顶栏快速 toggle 模块，立即保存。完整管理在 /settings/modules。 */
export default function ModuleQuickToggle() {
  const { me, refresh } = useAuth();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onClick(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const save = useMutation({
    mutationFn: (modules: Record<string, boolean>) =>
      api.authPatchPreferences({ modules }),
    onSuccess: () => {
      void refresh();
    },
  });

  if (!me) return null;
  const toggleable = MODULES.filter((m) => !m.forced);
  const enabledCount = toggleable.filter((m) => isModuleEnabled(me.preferences, m.id)).length;

  function toggle(moduleId: string) {
    const cur = isModuleEnabled(me!.preferences, moduleId);
    save.mutate({ [moduleId]: !cur });
  }

  return (
    <div className="relative" ref={wrapRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-1 text-xs text-zinc-600 dark:text-zinc-300 hover:text-zinc-900 dark:hover:text-white px-2 py-1 rounded border border-zinc-200 dark:border-zinc-700"
        title="快速切换功能模块"
      >
        <span>🧩</span>
        <span className="text-zinc-400 hidden sm:inline">{enabledCount}/{toggleable.length}</span>
        <span className="text-zinc-400 text-[10px]">▾</span>
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-64 rounded border bg-white dark:bg-zinc-900 shadow-md z-20 text-sm">
          <div className="px-3 py-2 text-xs text-zinc-500 border-b">
            勾选启用模块（即时生效）
          </div>
          <div className="py-1 max-h-80 overflow-y-auto">
            {toggleable.map((m) => {
              const enabled = isModuleEnabled(me.preferences, m.id);
              return (
                <label
                  key={m.id}
                  className={`flex items-center gap-2 px-3 py-1.5 cursor-pointer hover:bg-zinc-100 dark:hover:bg-zinc-800 ${
                    save.isPending ? "opacity-60" : ""
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={enabled}
                    disabled={save.isPending}
                    onChange={() => toggle(m.id)}
                  />
                  <span className="flex-1">{m.label}</span>
                </label>
              );
            })}
          </div>
          <div className="border-t py-1">
            <Link
              to="/settings/modules"
              onClick={() => setOpen(false)}
              className="block px-3 py-1.5 text-xs text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800"
            >
              完整管理 + 描述…
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}
